import { useCallback } from "react";
import { useNavigate } from "react-router";

import { useURL } from "~/contexts";

import { suppressEngagementStall } from "./engagement-stall-storage";
import { buildIntakeEntryQuery } from "./intake-routing";
import { isTreatmentAvailable, treatmentBySlug } from "./treatments";

export interface StartIntakeOptions {
  /** Treatment slug (e.g. "weight-loss"). Required. */
  treatment: string;
  /** Non-PHI source-page/surface id, preserved to intake + analytics (`src`). */
  source?: string;
  /** Non-PHI CTA position, preserved to intake + analytics (`cta`). */
  ctaLocation?: string;
}

interface StartIntakeNavOptions {
  /** RR navigation state (e.g. the First-Question handoff) carried to intake. */
  state?: unknown;
}

/**
 * Site-relative `/intake/...` path for a Start Intake CTA, or `null` when the
 * treatment is gated. THE single construction of that path — {@link
 * useStartIntake} navigates to it and {@link useIntakeEntryHref} renders it as
 * an href, so the two cannot drift. Add a query key here and both surfaces get
 * it; add it to one call site and you'd get an anchor pointing somewhere the
 * hook doesn't go, failing only on a pre-hydration click.
 */
function intakeEntryPath(opts: StartIntakeOptions): string | null {
  const { treatment, source, ctaLocation } = opts;

  if (!isTreatmentAvailable(treatment)) return null;

  const slug = treatmentBySlug(treatment)?.slug ?? treatment;
  const query = buildIntakeEntryQuery({ source, ctaLocation });
  return `/intake/${slug}${query ? `?${query}` : ""}`;
}

/**
 * The URL a Start Intake CTA points at, site-prefixed.
 *
 * Exists so a CTA can render as a real link rather than a click-only button
 * (ET-216): on a telehealth PDP the Get Started button REPLACES every other
 * affordance, so a click landing before hydration has to still go somewhere.
 * Returns `null` for a gated treatment — the caller then renders nothing at all.
 */
export function useIntakeEntryHref(opts: StartIntakeOptions): string | null {
  const buildUrl = useURL();
  const path = intakeEntryPath(opts);
  return path ? buildUrl(path) : null;
}

/**
 * ET-12 — the shared "Start Intake / Get Started" click contract, surface
 * agnostic. Any entry surface (landing page, tiles, hub, first-question module)
 * calls the returned `startIntake` so they all behave identically:
 *
 *  1. Gate on availability — a Coming Soon / unsupported treatment never starts
 *     intake (no event, no navigation) and returns `false`, leaving the caller
 *     on the source page.
 *  2. Route to `/intake/[treatment]`, preserving `src`/`cta` context in the URL.
 *
 * This hook used to emit `treatment_cta_clicked` here, carrying auth state and
 * the `hec_uid` reference id. That event is not part of the MarTech ingest spec
 * and went away with the Segment transport (ET-251), so the hook is now purely
 * gate-then-navigate. Returns `true` when intake started, `false` when gated.
 */
export function useStartIntake() {
  const navigate = useNavigate();
  const buildUrl = useURL();

  return useCallback(
    (opts: StartIntakeOptions, navOptions?: StartIntakeNavOptions): boolean => {
      const { treatment, source, ctaLocation } = opts;

      // (1) Availability gate — unavailable/unsupported treatments must not
      // start intake. Nothing fires; the caller stays on the source page.
      // (`intakeEntryPath` returns null on exactly that condition.)
      const path = intakeEntryPath(opts);
      if (!path) return false;

      const slug = treatmentBySlug(treatment)?.slug ?? treatment;

      // ET-109: any surface starting intake counts as "already clicked a page
      // CTA" for that treatment — suppress the engagement-stall modal for the
      // rest of the session so it never shows to someone who already
      // converted (including when this CTA IS the modal's own Get Started).
      suppressEngagementStall(slug, "page_cta_clicked");

      // (2) Route to the canonical intake path, carrying src/cta context.
      // (`treatment_cta_clicked` used to fire here; it isn't part of the
      // MarTech ingest spec and went away with the Segment transport.)
      navigate(buildUrl(path), navOptions);
      return true;
    },
    [navigate, buildUrl],
  );
}
